/**
 * shared/hooks/useOnlineStatus.ts
 * Detecta conexión a internet y operaciones pendientes en el outbox.
 */

import { useEffect, useState, useCallback } from 'react'
import { hasPendingOperations } from '@lib/outbox/outbox'
import { servidorAlcanzable, suscribirAlcance } from '@lib/net/reachability'

export interface OnlineStatus {
  isOnline: boolean
  pendingSync: number
}

// "En línea" significa que el SERVIDOR contesta, no que el equipo tenga red: con wifi
// y servidor caído el POS debe capturar la venta en la cola, no intentar cobrar al vacío.
const enLinea = () => navigator.onLine && servidorAlcanzable()

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(enLinea)
  const [pendingSync, setPendingSync] = useState(0)

  const refresh = useCallback(async () => {
    try {
      const has = await hasPendingOperations()
      setPendingSync(has ? 1 : 0)
    } catch {
      setPendingSync(0)
    }
  }, [])

  useEffect(() => {
    const sincronizar = () => {
      setIsOnline(enLinea())
      void refresh()
    }

    window.addEventListener('online', sincronizar)
    window.addEventListener('offline', sincronizar)
    // La sonda de identidad es quien sabe si el servidor contesta.
    const bajaAlcance = suscribirAlcance(sincronizar)

    // Refrescar cada 15s
    void refresh()
    const id = setInterval(() => void refresh(), 15_000)

    return () => {
      window.removeEventListener('online', sincronizar)
      window.removeEventListener('offline', sincronizar)
      bajaAlcance()
      clearInterval(id)
    }
  }, [refresh])

  return { isOnline, pendingSync }
}

// ── Otros hooks de utilidad ────────────────────────────────────────────────

/** Debounce para búsquedas rápidas (evita query en cada keystroke) */
export function useDebounce<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/** Reloj local que se actualiza cada segundo */
export function useLocalClock(): Date {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000)
    return () => clearInterval(id)
  }, [])
  return now
}
